import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, User, ForkKnife, Star, CaretDown, CaretUp, Buildings } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactRow = ({ contact, icon, badge }) => (
  <div className="flex items-center gap-3 py-2 px-3 bg-sand-50 rounded">
    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center">
      {icon}
    </span>
    <div className="flex-1 min-w-0">
      <div className="font-medium text-sm text-text-primary truncate">{contact.name || '—'}</div>
      {(contact.category || badge) && (
        <div className="text-[10px] tracking-wider uppercase text-text-secondary font-bold">{contact.category || badge}</div>
      )}
    </div>
    {contact.phone && (
      <a href={`tel:${contact.phone}`} className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-brand-primary hover:underline" data-testid={`contact-phone-${contact.id || contact.phone}`}>
        <Phone size={14} weight="bold" /> {contact.phone}
      </a>
    )}
  </div>
);

/**
 * CaptainPackageBookingsCard — captain dashboard view of all package bookings
 * auto-assigned to them. Each row expands to show full contact list (customer +
 * filled waiters + assigned professionals).
 */
const CaptainPackageBookingsCard = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [contactsByBooking, setContactsByBooking] = useState({});
  const [loadingContacts, setLoadingContacts] = useState(null);

  const fetch = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/captain/package-bookings`, { withCredentials: true });
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const toggleContacts = async (bookingId) => {
    if (openId === bookingId) {
      setOpenId(null);
      return;
    }
    setOpenId(bookingId);
    if (!contactsByBooking[bookingId]) {
      setLoadingContacts(bookingId);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/captain/package-bookings/${bookingId}/contacts`, { withCredentials: true });
        setContactsByBooking((m) => ({ ...m, [bookingId]: data }));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load contacts');
      } finally {
        setLoadingContacts(null);
      }
    }
  };

  if (loading) return null;

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="captain-bookings-card">
      <CardHeader>
        <CardTitle className="font-heading text-2xl flex items-center gap-2">
          <Buildings size={22} className="text-brand-primary" /> My Events
        </CardTitle>
        <CardDescription>
          Package bookings auto-assigned to you. Tap "View Contacts" to see customer & professional phone numbers for on-ground coordination.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {bookings.length === 0 ? (
          <p className="text-sm text-text-secondary py-3">No events assigned yet. New bookings will appear here once admin confirms them.</p>
        ) : (
          <div className="space-y-3">
            {bookings.map((b) => {
              const isOpen = openId === b.id;
              const contacts = contactsByBooking[b.id];
              return (
                <div key={b.id} className="border border-[#E8E1D5] rounded" data-testid={`captain-booking-${b.id}`}>
                  <div className="p-4 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-text-primary">{b.package_name || 'Package Booking'}</div>
                      <div className="text-xs text-text-secondary">
                        {b.event_date || '—'} · {b.event_location || 'Location TBD'} · ₹{Number(b.total || 0).toLocaleString('en-IN')}
                      </div>
                      <div className="mt-1 flex gap-2">
                        <span className={`text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 font-bold rounded ${
                          b.status === 'completed' ? 'bg-green-100 text-green-700' :
                          b.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>{b.status}</span>
                        {(b.waiter_slots || []).length > 0 && (
                          <span className="text-[10px] tracking-wider uppercase text-text-secondary font-bold">
                            {b.waiter_slots.filter(s => s.status === 'filled').length}/{b.waiter_slots.length} waiters confirmed
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => toggleContacts(b.id)}
                      variant="outline"
                      size="sm"
                      className="rounded-none border-[#E8E1D5] flex-shrink-0"
                      data-testid={`toggle-contacts-${b.id}`}
                    >
                      {isOpen ? <CaretUp size={14} className="mr-1" /> : <CaretDown size={14} className="mr-1" />}
                      {isOpen ? 'Hide' : 'View'} Contacts
                    </Button>
                  </div>
                  {isOpen && (
                    <div className="border-t border-[#E8E1D5] bg-sand-100/40 p-4 space-y-3" data-testid={`contacts-panel-${b.id}`}>
                      {loadingContacts === b.id ? (
                        <p className="text-sm text-text-secondary">Loading contacts...</p>
                      ) : contacts ? (
                        <>
                          {contacts.customer && (
                            <div>
                              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-text-secondary mb-1.5">Customer</div>
                              <ContactRow contact={contacts.customer} icon={<User size={14} weight="duotone" />} badge="Customer" />
                            </div>
                          )}
                          {contacts.waiters && contacts.waiters.length > 0 && (
                            <div>
                              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-text-secondary mb-1.5">Waiters / Servers ({contacts.waiters.length})</div>
                              <div className="space-y-1.5">
                                {contacts.waiters.map((w) => (
                                  <ContactRow key={`w-${w.id}-${w.slot_id || ''}`} contact={w} icon={<ForkKnife size={14} weight="duotone" />} badge={w.tier_name || 'Server'} />
                                ))}
                              </div>
                            </div>
                          )}
                          {contacts.professionals && contacts.professionals.length > 0 && (
                            <div>
                              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-text-secondary mb-1.5">Professionals ({contacts.professionals.length})</div>
                              <div className="space-y-1.5">
                                {contacts.professionals.map((p) => (
                                  <ContactRow key={`p-${p.id}-${p.slot_id || ''}`} contact={p} icon={<Star size={14} weight="duotone" />} badge={p.category || 'Professional'} />
                                ))}
                              </div>
                            </div>
                          )}
                          {!contacts.customer && (!contacts.waiters || contacts.waiters.length === 0) && (!contacts.professionals || contacts.professionals.length === 0) && (
                            <p className="text-sm text-text-secondary">No contacts yet — assignments will appear here once vendors confirm.</p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-text-secondary">Could not load contacts.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CaptainPackageBookingsCard;
